/**
 * Pre-click mirrors of the server's loop-start gates (migration 763).
 *
 * The server is the authority — these only keep a button from offering a move
 * the server will refuse, and say why in the same words.
 *
 *   catchball_create_loop / catchball_originate_loop
 *     — the cycle must be open (canon glossary, active-cycle predicate:
 *       status NOT IN ('completed', 'archived')).
 *   catchball_request_another_round
 *     — cycle open; the loop not already closed or accepted (canon 29 §2.3:
 *       another round CLOSES the loop; glossary v2.21: a second loop happens
 *       because the answer was not accepted); fewer loops on this cycle +
 *       track than the loop intent's maxIterations (null / 0 = unlimited);
 *       and only the ball holder may ask.
 */

const CLOSED_CYCLE_STATUSES = new Set(['completed', 'archived']);

export function isCycleOpenForLoops(cycle: { status?: string | null } | null | undefined): boolean {
  if (!cycle) return false;
  return !CLOSED_CYCLE_STATUSES.has(String(cycle.status ?? ''));
}

export const CYCLE_CLOSED_MESSAGE = 'This cycle is completed or archived, so it takes no new loops.';

interface LoopLike {
  id: string;
  cycleId: string;
  trackId?: string | null;
  state: string;
  closedAt?: Date | string | null;
  ballHolderUserId?: string | null;
  intent?: { maxIterations?: number | null } | null;
}

/**
 * Why "another round" is not available right now, or null when the server
 * would accept it. `loops` is every loop of the cycle the caller has loaded.
 */
export function anotherRoundBlocker(
  loop: LoopLike,
  loops: readonly LoopLike[],
  cycle: { status?: string | null } | null | undefined,
  userId: string | null | undefined,
): string | null {
  if (loop.state === 'accepted') return 'This loop is already accepted — another round is not available.';
  if (loop.state === 'closed' || (loop.closedAt && loop.state !== 'rejected')) {
    return 'This loop is already closed — another round is not available.';
  }
  if (cycle && !isCycleOpenForLoops(cycle)) return CYCLE_CLOSED_MESSAGE;
  if (!userId || loop.ballHolderUserId !== userId) {
    return 'Take the ball first — only the current ball holder can start another round.';
  }
  const max = loop.intent?.maxIterations;
  if (typeof max === 'number' && max > 0) {
    const rounds = loops.filter(
      (l) => l.cycleId === loop.cycleId && (l.trackId ?? null) === (loop.trackId ?? null),
    ).length;
    if (rounds >= max) {
      return `This loop allows at most ${max} round${max === 1 ? '' : 's'} and ${rounds} ${
        rounds === 1 ? 'has' : 'have'
      } run.`;
    }
  }
  return null;
}
